import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from '../../../hooks/useTranslation'
import { Button } from '../../../lib/ui/button'
import ActiveTileHeader from './ActiveTileHeader'
import { useActionPlan } from '../ActionPlanProvider'
import { apiPatch } from '../../../util/api'
import { cn } from '../../../lib/utils'
import { getUserTrackerSections } from '../index'
import { TimeIcon, WellnessIcon } from './icons'
import { CoachInsights, Orientation } from '~/components/icons/YnsIcon'
import HeadshotCircle from '~/components/coach_services/MeetYourCoach/HeadshotCircle'
import { AddedIcon } from '~/components/CoachCandidateView/ActionPlan/icons'

import {
  BrainResearch as AssessmentIcon,
  Compass as ArticleIcon,
  MagicWand as AylaIcon,
  Bicycle as ExerciseIcon,
  Golf as ShortCourseIcon,
  AreaSearch as SearchToolIcon,
} from 'iconoir-react'

import {
  SpeakerWaveIcon as AudioIcon,
  ClipboardDocumentCheckIcon as ChecklistIcon,
  ComputerDesktopIcon as WebinarIcon,
  PuzzlePieceIcon as QuizIcon,
  WrenchScrewdriverIcon as BuilderIcon,
  UserCircleIcon as CoachServiceIcon,
  DocumentIcon as SampleDocIcon,
  VideoCameraIcon as VideoIcon,
} from '@heroicons/react/24/outline'

export const itemTypeIconMap = {
  article: <ArticleIcon />,
  video: <VideoIcon />,
  quiz: <QuizIcon />,
  exercise: <ExerciseIcon />,
  ayla: <AylaIcon />,
  webinar: <WebinarIcon />,
  wellness_webinar: <WellnessIcon />,
  assessment: <AssessmentIcon />,
  audio: <AudioIcon />,
  short_course: <ShortCourseIcon />,
  coach_service: <CoachServiceIcon />,
  builder: <BuilderIcon />,
  search_tool: <SearchToolIcon />,
  checklist: <ChecklistIcon />,
  sample: <SampleDocIcon />,
  workshop: <ShortCourseIcon />,
  insights: <CoachInsights />,
  welcome: <Orientation />,
  coach_resource: <CoachServiceIcon />,
}

const ActiveTile = ({ tileInfo }) => {
  const sectionName = Object.keys(tileInfo)[0]
  const { t } = useTranslation()

  const incomingTasks = tileInfo[sectionName]?.subsections || []
  const [tasks, setTasks] = useState(incomingTasks)

  useEffect(() => {
    setTasks(incomingTasks)
  }, [incomingTasks])

  const completedTasks = useMemo(() => {
    return tasks.filter(
      (task) => task.completed || task.skipped || task.coach_skipped,
    ).length
  }, [tasks])

  const sortedSubsections = useMemo(() => {
    return [...tasks].sort((a, b) => {
      const aSkipped = a.skipped || a.coach_skipped
      const bSkipped = b.skipped || b.coach_skipped

      if (aSkipped && !bSkipped) return 1
      if (!aSkipped && bSkipped) return -1
      return 0
    })
  }, [tasks])

  function calculateCompletedTasks() {
    return t('completed_tasks', {
      completed: completedTasks,
      total: tasks.length,
    })
  }

  const updateTask = (slug, updates) => {
    setTasks((prev) =>
      prev.map((task) => (task.slug === slug ? { ...task, ...updates } : task)),
    )
  }

  return (
    <div className="section-tile mb-[24px] bg-[#E0F2F9] p-4 lg:p-8">
      <ActiveTileHeader
        sectionName={sectionName}
        calculateCompletedTasks={calculateCompletedTasks}
        tileInfo={tileInfo}
        t={t}
      />

      <div className="relative my-6 flex flex-row rounded-[16px]">
        <div className="flex w-full flex-col flex-wrap gap-[24px] rounded-[16px]">
          {sortedSubsections.map((tile, idx) => {
            return (
              <TrackItem
                key={tile.slug || `expanded-${idx}`}
                tile={tile}
                completedTasks={completedTasks}
                totalTasksCount={tasks.length}
                updateTask={updateTask}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default ActiveTile

const TrackItem = ({ tile, completedTasks, totalTasksCount, updateTask }) => {
  const { t } = useTranslation()
  const [isSkipped, setIsSkipped] = useState(false)
  const [skipping, setSkipping] = useState(false)
  const [isCompleted, setIsCompleted] = useState(false)

  useEffect(() => {
    setIsSkipped(tile.skipped)
    setIsCompleted(tile.completed)
  }, [tile])

  const refreshTrackerSections = () => {
    getUserTrackerSections()
  }

  const handleLastItemRefresh = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    setTimeout(() => {
      refreshTrackerSections()
    }, 1000)
  }

  const skipItemAnimation = (nextSkipped) => {
    if (nextSkipped) {
      setSkipping(true)

      setTimeout(() => {
        setIsSkipped(true)
        updateTask(tile.slug, { skipped: true })

        const nextCompletedTasks = completedTasks + 1

        if (nextCompletedTasks === totalTasksCount) {
          handleLastItemRefresh()
        } else {
          refreshTrackerSections()
        }

        setSkipping(false)
      }, 2550)
    } else {
      setIsSkipped(false)
      updateTask(tile.slug, { skipped: false })
      refreshTrackerSections()
    }
  }

  const skipItem = async () => {
    const res = await apiPatch('/trackers/skip_item', { slug: tile.slug })

    if (res.status !== 200) {
      throw new Error('res not ok')
    }

    const nextSkipped = !isSkipped
    skipItemAnimation(nextSkipped)

    return res
  }

  const markItemComplete = async () => {
    const res = await apiPatch('/trackers/mark_item_complete', {
      achievement: tile.achievement,
    })

    if (res.status !== 200) {
      throw new Error('res not ok')
    }

    const nextCompletedTasks = completedTasks + 1

    if (nextCompletedTasks === totalTasksCount) {
      handleLastItemRefresh()
    } else {
      setIsCompleted(true)
      updateTask(tile.slug, { completed: true })
      refreshTrackerSections()
    }

    return res
  }

  const icon = itemTypeIconMap[tile.type]

  return (
    <TileContent
      tile={tile}
      icon={icon}
      t={t}
      isSkipped={isSkipped}
      skipItem={skipItem}
      markItemComplete={markItemComplete}
      skipping={skipping}
      isCompleted={isCompleted}
    />
  )
}

const TileContent = ({
  tile,
  icon,
  skipItem,
  markItemComplete,
  skipping,
  isSkipped,
  isCompleted,
  t,
}) => {
  const isCoachSkipped = tile.coach_skipped
  const isCoachAdded = tile.coach_added
  const isCoachResource = tile.slug.includes('coach_resource')
  const skippedClass = isSkipped || isCoachSkipped
  const background = skippedClass ? '#373737' : '#FFFFFF'
  const isCoachService = tile['coach_service']
  const { completionAnimationUrl, setConversationStarter } = useActionPlan()
  const summaryUrl = `${tile.url}/summary`
  // isCompleted checks for achievement (this can fire if you just mark as completed on action plan).
  // isFullyCompleted checks for if the document is actually completed.
  const isFullyCompleted = Boolean(tile.is_fully_completed)
  const isClassicExercise =
    tile.type === 'exercise' &&
    ['professional-skills', 'accomplishment-statements'].includes(tile.slug)
  const isClassicResourceType = [
    'assessment',
    'workshop',
    'short_course',
  ].includes(tile.type)
  const fullyCompletedClassicResources =
    isFullyCompleted && (isClassicResourceType || isClassicExercise)
  const destinationUrl = fullyCompletedClassicResources ? summaryUrl : tile.url
  const getItemActionLabel = () => {
    if (
      isCompleted &&
      isFullyCompleted &&
      (tile.type === 'quiz' ||
        fullyCompletedClassicResources ||
        (tile.type === 'exercise' && !isClassicExercise))
    ) {
      return t('item_type_actions.view_results')
    }
    return t(`item_type_actions.${tile.type}`)
  }

  const itemActionLabel = getItemActionLabel()

  return (
    <div
      className={cn(
        'track-item relative flex flex-col gap-6 rounded-[16px] bg-white p-[16px] pl-[32px] lg:grid lg:grid-cols-12 lg:gap-0 lg:pl-[16px]',
        skippedClass || skipping ? 'skipped-track-item' : '',
      )}
    >
      <div
        className={`absolute left-0 top-0 z-50 flex h-full w-[16px] flex-col rounded-bl-[16px] rounded-tl-[16px] ${
          isCoachService ? 'bg-intoo-green-coachcta' : 'bg-primary'
        }`}
      ></div>

      <SkipTileLogic tile={tile} isSkipped={isSkipped} skipItem={skipItem} />

      <div
        className={cn(
          'absolute left-0 top-0 w-full cursor-pointer rounded-[16px]',
        )}
        style={{
          background,
          height: skipping ? '100%' : '0px',
          opacity: skipping ? '90%' : '0%',
          transition: 'opacity 2s ease-in-out',
        }}
      ></div>

      <div className="lg:col-span-3">
        <div className="flex h-full flex-col items-center justify-center">
          {isCoachResource ? (
            <HeadshotCircle
              coachImage={tile.image?.path}
              isCoachResource={true}
            />
          ) : (
            <div
              className="h-[120px] w-full overflow-hidden md:w-[80%]"
              style={{
                backgroundImage:
                  tile.image && tile.image.path
                    ? `url(${tile.image.path})`
                    : '',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
              alt={tile.image?.title ? tile.image.title : tile.name}
            ></div>
          )}
        </div>
      </div>

      <div className="col-span-5 flex flex-col gap-2 lg:pr-6 xl:col-span-6">
        <div className="subtitle !text-xl">{tile.name}</div>
        {tile.description}
        <div className="flex md:flex-row">
          <div className="flex flex-col items-center justify-center gap-2 border-r border-cod-gray-200 pr-6">
            <TimeIcon variant={isCoachService ? 'coach' : 'primary'} />
            <div
              className={`capitalize ${
                isCoachService ? 'text-intoo-green-coachcta' : 'text-primary'
              }`}
            >
              {tile.length}
            </div>
          </div>

          <div
            className={`flex flex-col items-center justify-center gap-2 ${
              isCoachAdded ? 'px-3' : 'pl-6'
            }`}
          >
            {React.cloneElement(icon, {
              className: isCoachService
                ? 'text-intoo-green-coachcta'
                : 'text-primary',
              height: '24',
            })}
            <div
              className={`capitalize ${
                isCoachService ? 'text-intoo-green-coachcta' : 'text-primary'
              }`}
            >
              {itemActionLabel}
            </div>
          </div>

          {isCoachAdded && (
            <div className="flex flex-col items-center justify-center gap-2 border-l border-cod-gray-200 pl-6">
              <AddedIcon className="text-intoo-green-coachcta" height="24" />
              <div className="capitalize text-intoo-green-coachcta">
                {t('coach_added', 'Coach Added')}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="col-span-3 flex h-full flex-col justify-center xl:col-span-2">
        <div
          className={`flex flex-col items-center justify-center ${
            isCompleted ? '' : 'gap-6'
          } text-center`}
        >
          <a
            href={destinationUrl}
            className={cn(
              'w-full',
              tile.conversation_starter && 'open-chat-modal',
            )}
            data-toggle={tile.conversation_starter && 'modal'}
            data-target={tile.conversation_starter && '#coach_chat_list'}
            target={isCoachResource ? '_blank' : undefined}
            rel={isCoachResource ? 'noreferrer' : undefined}
          >
            {isCoachService ? (
              <Button
                variant="coachcta"
                title={t('view') + ' ' + tile.name}
                href={destinationUrl}
                className="w-full text-[14px]"
                onClick={() => {
                  tile.conversation_starter &&
                    setConversationStarter(tile.conversation_starter)
                }}
              >
                {t(`item_type_actions.${tile.type}`)}
                {/* CTS-9291 — include tile name in accessible name (title is not sufficient for all AT) */}
                <span className="sr-only">, {tile.name}</span>
                <span className="ml-[8px]" aria-hidden="true">
                  {React.cloneElement(icon, { variant: 'light', height: '24' })}
                </span>
              </Button>
            ) : (
              <Button
                title={t('view') + ' ' + tile.name}
                href={destinationUrl}
                className="w-full"
                target="_blank"
                rel="noreferrer"
              >
                {itemActionLabel}
                <span className="sr-only">, {tile.name}</span>
                <span className="ml-[8px]" aria-hidden="true">
                  {React.cloneElement(icon, { variant: 'light', height: '24' })}
                </span>
              </Button>
            )}
          </a>

          {isCompleted ? (
            <img
              className="w-20"
              src={completionAnimationUrl}
              alt="Article complete"
            />
          ) : (
            <Button
              variant="outline"
              onClick={markItemComplete}
              className="w-full"
            >
              {t('item_type_actions.mark_complete', 'Mark as Completed')}
              <span className="sr-only">, {tile.name}</span>
            </Button>
          )}
        </div>
      </div>

      <div className="justify-top col-span-1 h-full text-right">
        {!isCompleted && !isSkipped && (
          <Button
            onClick={skipItem}
            variant="link"
            className="!h-auto cursor-pointer p-0 text-base !underline"
          >
            {t('skip', 'Skip')}
          </Button>
        )}
      </div>
    </div>
  )
}

const SkipTileLogic = ({ tile, isSkipped, skipItem }) => {
  const isCoachSkipped = tile.coach_skipped
  const skippedClass = isSkipped || isCoachSkipped
  const background = skippedClass ? '#373737' : '#FFFFFF'
  const { t } = useTranslation()
  const [skippedText, setSkippedText] = useState(
    t('task_skipped', 'Task Skipped'),
  )

  if (isCoachSkipped) {
    return (
      <div
        className="absolute left-0 top-0 z-20 h-full w-full rounded-[16px] opacity-90"
        style={{
          background,
          display: 'block',
        }}
      >
        <div className="flex h-full items-center justify-center">
          <div className="font-roboto-slab text-2xl font-bold text-white">
            {t('task_skipped_by_coach', 'Task Skipped by Coach')}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="absolute left-0 top-0 z-20 h-full w-full cursor-pointer rounded-[16px] opacity-90"
      style={{
        background,
        display: skippedClass ? 'block' : 'none',
      }}
      onMouseOver={() => setSkippedText(t('unskip_task', 'Unskip Task'))}
      onMouseOut={() => setSkippedText(t('task_skipped', 'Task Skipped'))}
      onClick={() => skipItem()}
    >
      <div className="flex h-full items-center justify-center">
        <div className="font-roboto-slab text-2xl font-bold text-white">
          {skippedText}
        </div>
      </div>
    </div>
  )
}
